import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Layout, SectionContent, SectionHead } from "@layouts/MainLayout";
import { useTranslation } from "react-i18next";
import TableList from "@components/TableList";
import { RowSelectionState } from "@tanstack/react-table";
import { columns } from "@pages/user/bindings/_components/columns";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Binding,
  BindingsList,
  DeleteBindingResponse,
} from "@lib/types/binding";
import { getBindings } from "@lib/api/queries/bindings";
import CustomTabs, {
  TabGroup,
  TabName,
  TabContent,
} from "@components/CustomTab";
import CreateBinding from "@pages/user/bindings/_components/createBinding";
import useHandleError from "@lib/utils/errorHook";
import { AxiosError } from "axios";
import { useSnackbar } from "@context/SnackBarProvider";
import ViewBinding from "@pages/user/bindings/_components/viewBinding";
import { deleteBinding } from "@lib/api/mutations/binding";
import PromptDialog from "@components/PromptDialog";
import UpdateBinding from "@pages/user/bindings/_components/updateBinding";

const BindingsMain: React.FC = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSnackbar } = useSnackbar();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [addBindingDialogOpen, setAddBindingDialogOpen] = useState(false);
  const [selectedBinding, setSelectedBinding] = useState<Binding | undefined>(
    undefined
  );
  const [bindingToEdit, setBindingToEdit] = useState<Binding | undefined>(
    undefined
  );

  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [updateBindingDialogOpen, setUpdateBindingDialogOpen] = useState(false);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const {
    data: bindings,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery<BindingsList>({
    queryKey: ["bindings"],
    queryFn: () => getBindings(),
    refetchOnWindowFocus: false,
  });

  const { mutate: deleteBindingfn, isPending: isDeletingDriver } = useMutation({
    mutationFn: deleteBinding,
    onSuccess: (data: DeleteBindingResponse) => {
      showSnackbar(t("user.bindings.deleteSuccess"), "success");

      setSelectedBinding(undefined);
      refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  useEffect(() => {
    if (error instanceof AxiosError && error.response) {
      const errorDetails = handleError(error.response.data);
      errorDetails && showSnackbar(errorDetails.message, "error");
    }
  }, [error]);

  const handleViewBinding = (binding: Binding) => {
    setSelectedBinding(binding);
    setViewDialogOpen(true);
  };

  const confirmDeleteBinding = () => {
    if (!selectedBinding) return;
    deleteBindingfn(selectedBinding.id);
    setShowDeletePrompt(false);
  };

  const handleDeleteBinding = (binding: Binding) => {
    setSelectedBinding(binding);
    setShowDeletePrompt(true);
  };

  return (
    <Layout>
      <SectionHead title={t("user.bindings.header")}>
        <>
          <Button
            variant="contained"
            onClick={() => setAddBindingDialogOpen(true)}
            color="primary"
          >
            {t("user.bindings.add")}
          </Button>
        </>
      </SectionHead>

      <SectionContent>
        <>
          {!error && (
            <div className="flex flex-col items-center justify-between">
              <CustomTabs>
                <TabGroup>
                  <TabName>{t("user.bindings.one_time")}</TabName>
                  <TabContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <TableList
                        data={
                          bindings?.bindings.filter(
                            (binding) => binding.binding_type === "one_time"
                          ) || []
                        }
                        columns={columns(
                          setBindingToEdit,
                          handleViewBinding,
                          handleDeleteBinding,
                          setUpdateBindingDialogOpen,
                          "one_time"
                        )}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        isLoading={isLoading || isFetching}
                      />
                    </Box>
                  </TabContent>
                </TabGroup>
                <TabGroup>
                  <TabName>{t("user.bindings.expiring")}</TabName>
                  <TabContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <TableList
                        data={
                          bindings?.bindings.filter(
                            (binding) => binding.binding_type === "expiring"
                          ) || []
                        }
                        columns={columns(
                          setBindingToEdit,
                          handleViewBinding,
                          handleDeleteBinding,
                          setUpdateBindingDialogOpen,
                          "expiring"
                        )}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        isLoading={isLoading || isFetching}
                      />
                    </Box>
                  </TabContent>
                </TabGroup>
                <TabGroup>
                  <TabName>{t("user.bindings.recurring")}</TabName>
                  <TabContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <TableList
                        data={
                          bindings?.bindings.filter(
                            (binding) => binding.binding_type === "recurring"
                          ) || []
                        }
                        columns={columns(
                          setBindingToEdit,
                          handleViewBinding,
                          handleDeleteBinding,
                          setUpdateBindingDialogOpen,
                          "recurring"
                        )}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        isLoading={isLoading || isFetching}
                      />
                    </Box>
                  </TabContent>
                </TabGroup>
              </CustomTabs>
            </div>
          )}

          <CreateBinding
            createBindingDialogOpen={addBindingDialogOpen}
            setCreateBindingDialogOpen={setAddBindingDialogOpen}
            refetch={refetch}
          />

          <PromptDialog
            handleClose={() => setShowDeletePrompt(false)}
            handleConfirm={() => confirmDeleteBinding()}
            isOpen={showDeletePrompt}
            title={t("user.bindings.delete")}
            description={t("user.bindings.deletePrompt", {
              id: selectedBinding?.id,
            })}
          />

          <UpdateBinding
            bindingData={bindingToEdit}
            updateBindingDialogOpen={updateBindingDialogOpen}
            setUpdateBindingDialogOpen={setUpdateBindingDialogOpen}
            refetch={refetch}
          />
          <ViewBinding
            viewBindingDialogOpen={viewDialogOpen}
            setViewBindingDialogOpen={setViewDialogOpen}
            bindingData={selectedBinding}
          />
        </>
      </SectionContent>
    </Layout>
  );
};

export default BindingsMain;
